import React, { useState } from "react";
import "../src/theme/app.scss";
import "../src/styles/global.scss";
import styles from "../src/styles/Guide.module.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "nprogress/nprogress.css";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "../src/theme/theme";
import { wrapper } from "../src/store/store";
import AuthProvider from "../src/firebase/AuthProvider";
import { appWithTranslation } from "next-i18next";
import App from "next/app";
import AuthLayout from "../src/layout/AuthLayout";
import {
  ENHANCE_GUEST_QUIZ,
  PAYMENT,
  ASIP,
  AUTH,
  ENHANCE_DASHBOARD,
  ENHANCE_PDF,
  ENHANCE_PDF_VIEWER,
  ENHANCE_QUIZ,
  GUIDES,
  ONBOARD_PROFILE,
  PROFILE,
} from "../src/constants/routes";
import dynamic from "next/dynamic";
import smoothscroll from "smoothscroll-polyfill";
import { firebaseApp } from "../src/firebase/firebaseClient";
import PrimaryLayout from "../src/layout/PrimaryLayout";
import { CacheProvider, EmotionCache } from "@emotion/react";
import createEmotionCache from "../src/theme/createEmotionCache";
import ZohoChat from "../src/firebase/ZohoChat";
import GoogleAnalytics from "../src/firebase/GoogleAnalytics";
import PageSense from "../src/firebase/PageSense";
import { AppProps } from "next/app";
import GoogleTagManager from "../src/firebase/GoogleTagManager";
import GuideQuestionsProvider from "../src/firebase/GuideQuestionsProvider";
import GoogleMapsProvider from "../src/firebase/GoogleMapsProvider";
import { ThemeProvider } from "@mui/material/styles";

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();
interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

const TopProgressBar = dynamic(
  () => {
    return import("../src/components/TopProgressBar");
  },
  { ssr: false }
);

function PwgApp(props: MyAppProps) {
  const [loaded, setLoaded] = useState(false);
  const {
    Component,
    emotionCache = clientSideEmotionCache,
    pageProps,
    router,
  } = props;
  React.useEffect(() => {
    if (typeof window !== undefined) {
      smoothscroll.polyfill();
      import("firebase/performance").then((result) => {
        const perf = result.getPerformance(firebaseApp);
      });
      setLoaded(true);
    }
  }, []);
  const getLayout = (pathname: string) => {
    if (
      pathname.includes(AUTH) ||
      pathname.includes(PAYMENT) ||
      pathname.includes(ENHANCE_GUEST_QUIZ)
    ) {
      return (
        <AuthLayout>
          <Component {...pageProps} />
        </AuthLayout>
      );
    } else if (
      pathname.includes(PROFILE) ||
      pathname.includes(ONBOARD_PROFILE)
    ) {
      return (
        <GoogleMapsProvider>
          <PrimaryLayout>
            <Component {...pageProps} />
          </PrimaryLayout>
        </GoogleMapsProvider>
      );
    } else if (pathname.includes(ASIP)) {
      return <Component {...pageProps} />;
    } else if (pathname.includes(ENHANCE_QUIZ)) {
      return (
        <GoogleMapsProvider>
          <PrimaryLayout
            blockClass={styles["enhance-block"]}
            bodyClass={styles["enhance-body"]}
            hideFooter
          >
            <Component {...pageProps} />
          </PrimaryLayout>
        </GoogleMapsProvider>
      );
    } else if (
      pathname.includes(ENHANCE_DASHBOARD) ||
      pathname.includes(ENHANCE_PDF_VIEWER)
    ) {
      return (
        <PrimaryLayout
          blockClass={`${styles["enhance-block"]} ${styles["enhance-block-white"]}`}
          bodyClass={styles["enhance-body"]}
          contentClass={styles["content-white"]}
          hideFooter
        >
          <Component {...pageProps} />
        </PrimaryLayout>
      );
    } else if (pathname.includes(ENHANCE_PDF)) {
      return <Component {...pageProps} />;
    } else if (pathname.includes(GUIDES)) {
      return (
        <GuideQuestionsProvider>
          <GoogleMapsProvider>
            <PrimaryLayout>
              <Component {...pageProps} />
            </PrimaryLayout>
          </GoogleMapsProvider>
        </GuideQuestionsProvider>
      );
    } else {
      return (
        <PrimaryLayout>
          <Component {...pageProps} />
        </PrimaryLayout>
      );
    }
  };
  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={theme}>
        <ZohoChat />
        <GoogleTagManager />
        <GoogleAnalytics />
        <PageSense />
        <TopProgressBar />
        <CssBaseline />
        <AuthProvider>{loaded && getLayout(router.pathname)}</AuthProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

PwgApp.getInitialProps = async (appContext) => ({
  ...(await App.getInitialProps(appContext)),
});

const wrappedWithI18n = appWithTranslation(PwgApp);
export default wrapper.withRedux(wrappedWithI18n);
